import {
  useEffect,
  useRef,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from 'react';
import {
  deriveEstimateDefaultsFromTemplate,
  templateLineItemsToEditable,
  type EstimateTemplateDefaults,
} from '@/routes/_dashboard/dashboard/estimates/_shared';
import { useTermsTemplates } from '@/hooks/settings/useTermsTemplates';
import { resolveTermsSelection, type TermsSelection, type TermsSelectionRecord } from '@/lib/terms';
import { toast } from 'sonner';

interface UseEstimateTemplateSeedArgs {
  selectedTemplate?:
    | ({
        id: string;
        notes?: string | null;
        lineItems: Parameters<typeof templateLineItemsToEditable>[0];
      } & Parameters<typeof deriveEstimateDefaultsFromTemplate>[0] &
        TermsSelectionRecord)
    | null;
  setNotes: Dispatch<SetStateAction<string>>;
  setLineItems: Dispatch<SetStateAction<ReturnType<typeof templateLineItemsToEditable>>>;
  setTerms: Dispatch<SetStateAction<TermsSelection>>;
  /**
   * Applies the document defaults the template carries — its job type, deposit
   * policy, recurrence and the rest. Without it a template describes only WHAT
   * work it sells and nothing about the terms it sells under.
   */
  applyTemplateDefaults: (defaults: EstimateTemplateDefaults) => void;
  /**
   * Records which fields the template authored, so the company/billing defaults
   * — which resolve from their own queries and can land either side of this —
   * do not overwrite them.
   */
  templateAuthoredFieldsRef?: MutableRefObject<Set<string>>;
}

export function useEstimateTemplateSeed({
  selectedTemplate,
  setNotes,
  setLineItems,
  setTerms,
  applyTemplateDefaults,
  templateAuthoredFieldsRef,
}: UseEstimateTemplateSeedArgs) {
  const { data: termsTemplates = [], isPending: termsPending } = useTermsTemplates();
  const appliedTemplateRef = useRef('');
  const rejectedTemplateSeedRef = useRef('');
  // Terms seed separately from notes/line items: they depend on a second query,
  // and holding the whole seed back until it lands would stall the form.
  const seededTermsForTemplateRef = useRef('');

  useEffect(() => {
    if (!selectedTemplate) {
      rejectedTemplateSeedRef.current = '';
      return;
    }
    const templateSeedKey = `${selectedTemplate.id}:${JSON.stringify(selectedTemplate.lineItems ?? [])}`;

    if (rejectedTemplateSeedRef.current === templateSeedKey) return;

    if (appliedTemplateRef.current !== selectedTemplate.id) {
      const defaults = deriveEstimateDefaultsFromTemplate(selectedTemplate);
      let seededLineItems: ReturnType<typeof templateLineItemsToEditable>;
      try {
        seededLineItems = templateLineItemsToEditable(selectedTemplate.lineItems);
      } catch (error) {
        rejectedTemplateSeedRef.current = templateSeedKey;
        toast.error(
          error instanceof Error
            ? error.message
            : 'This estimate template contains an incompatible membership line.',
        );
        return;
      }
      rejectedTemplateSeedRef.current = '';
      appliedTemplateRef.current = selectedTemplate.id;
      if (templateAuthoredFieldsRef) {
        // `notes` unconditionally: applying a template always sets them, even
        // to blank, so the company default must not fill them back in.
        templateAuthoredFieldsRef.current = new Set([...Object.keys(defaults), 'notes']);
      }
      setNotes(selectedTemplate.notes ?? '');
      setLineItems(seededLineItems);
      applyTemplateDefaults(defaults);
    }

    // The estimate template stores a T&C *selection* with no snapshot, so it has
    // to be resolved against the repository. Resolving against a still-loading
    // (empty) list would fall back to blank terms and — because the seed is
    // one-shot — lock that in, quietly dropping the entry the template pinned.
    if (termsPending) return;
    if (seededTermsForTemplateRef.current === selectedTemplate.id) return;
    seededTermsForTemplateRef.current = selectedTemplate.id;
    setTerms(resolveTermsSelection(selectedTemplate, termsTemplates));
  }, [
    selectedTemplate,
    setNotes,
    setLineItems,
    setTerms,
    applyTemplateDefaults,
    templateAuthoredFieldsRef,
    termsTemplates,
    termsPending,
  ]);
}
